import React, { useCallback, useState } from "react";
import { Pencil, Plus, StickyNote, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { FormFooter } from "@/components/FormFooter";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { clientesAPI, type Cliente, type ClienteNota } from "@/lib/api";
import {
  notaFechaLabel,
  notaTituloLabel,
  isAbsAdminUser,
  type NotasUser,
} from "./clienteEstadoCuentaNotas.permissions";

export type { NotasUser } from "./clienteEstadoCuentaNotas.permissions";

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
}

interface ClienteEstadoCuentaNotasProps {
  cliente: Cliente;
  user?: NotasUser;
}

export function ClienteEstadoCuentaNotas({ cliente, user }: ClienteEstadoCuentaNotasProps) {
  const isAdmin = isAbsAdminUser(user);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"list" | "form">("list");
  const [notas, setNotas] = useState<ClienteNota[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [titulo, setTitulo] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [editing, setEditing] = useState<ClienteNota | null>(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [selectedNotaIds, setSelectedNotaIds] = useState<Set<number>>(new Set());
  const [openNotaId, setOpenNotaId] = useState("");
  const selectedCount = selectedNotaIds.size;

  const fetchNotas = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await clientesAPI.listNotas(cliente.codigo);
      setNotas(rows);
      setSelectedNotaIds((prev) => {
        if (prev.size === 0) return prev;
        const valid = new Set(rows.map((n) => n.id));
        const next = new Set([...prev].filter((id) => valid.has(id)));
        return next.size === prev.size ? prev : next;
      });
    } catch (error) {
      console.error("Error loading notas:", error);
      toast.error(errorMessage(error, "Error al cargar las notas"));
    } finally {
      setLoading(false);
    }
  }, [cliente.codigo]);

  const resetDialog = () => {
    setMode("list");
    setEditing(null);
    setTitulo("");
    setCuerpo("");
    setOpenNotaId("");
    setSelectedNotaIds(new Set());
    setPendingDeleteIds([]);
  };

  const toggleNotaSelected = (id: number, checked: boolean) => {
    setSelectedNotaIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const stopAccordionToggle = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const openList = () => {
    resetDialog();
    setOpen(true);
    void fetchNotas();
  };

  const openCreate = () => {
    setEditing(null);
    setTitulo("");
    setCuerpo("");
    setMode("form");
  };

  const startEdit = (nota: ClienteNota) => {
    setEditing(nota);
    setTitulo(nota.titulo || "");
    setCuerpo(nota.cuerpo);
    setOpenNotaId(String(nota.id));
  };

  const cancelInlineEdit = () => {
    setEditing(null);
    setTitulo("");
    setCuerpo("");
  };

  const backToList = () => {
    setMode("list");
    setEditing(null);
    setTitulo("");
    setCuerpo("");
  };

  const handleSave = async () => {
    const title = titulo.trim();
    const text = cuerpo.trim();
    if (!title) {
      toast.error("El título de la nota es requerido");
      return;
    }
    if (!text) {
      toast.error("El texto de la nota es requerido");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await clientesAPI.updateNota(cliente.codigo, editing.id, {
          titulo: title,
          cuerpo: text,
        });
        toast.success("Nota actualizada");
        setEditing(null);
        setTitulo("");
        setCuerpo("");
      } else {
        await clientesAPI.createNota(cliente.codigo, { titulo: title, cuerpo: text });
        toast.success("Nota creada");
        backToList();
      }
      await fetchNotas();
    } catch (error) {
      toast.error(errorMessage(error, "Error al guardar la nota"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (pendingDeleteIds.length === 0) return;
    const ids = pendingDeleteIds;
    try {
      await Promise.all(ids.map((id) => clientesAPI.deleteNota(cliente.codigo, id)));
      toast.success(ids.length === 1 ? "Nota eliminada" : `${ids.length} notas eliminadas`);
      const deleted = new Set(ids);
      if (openNotaId && deleted.has(Number(openNotaId))) setOpenNotaId("");
      if (editing && deleted.has(editing.id)) cancelInlineEdit();
      setPendingDeleteIds([]);
      setSelectedNotaIds(new Set());
      await fetchNotas();
    } catch (error) {
      toast.error(errorMessage(error, "Error al eliminar la nota"));
      await fetchNotas();
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        onClick={openList}
        title="Notas"
      >
        <StickyNote className="h-4 w-4" />
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) resetDialog();
        }}
      >
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          {mode === "list" ? (
            <>
              <DialogHeader>
                <DialogTitle>Notas · {cliente.nombre}</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-end gap-2">
                {isAdmin && selectedCount > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={`flex items-center gap-1 ${deleteIconButtonClass}`}
                    title="Eliminar seleccionadas"
                    aria-label="Eliminar seleccionadas"
                    onClick={() => setPendingDeleteIds([...selectedNotaIds])}
                  >
                    <Trash2 className="h-4 w-4" />
                    ({selectedCount})
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={openCreate}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Nota
                </Button>
              </div>
              {loading ? (
                <p className="text-sm text-muted-foreground text-center py-6">Cargando notas...</p>
              ) : notas.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No hay notas para este cliente
                </p>
              ) : (
                <Accordion
                  type="single"
                  collapsible
                  value={openNotaId}
                  onValueChange={(value) => {
                    setOpenNotaId(value);
                    if (editing && String(editing.id) !== value) {
                      cancelInlineEdit();
                    }
                  }}
                  className="max-h-[calc(10*2.75rem)] overflow-y-auto overscroll-contain pr-1 rounded-md border bg-muted/20"
                >
                  {notas.map((nota) => {
                    const itemId = String(nota.id);
                    const isEditingThis = isAdmin && editing?.id === nota.id;
                    const fecha = notaFechaLabel(nota);
                    const tituloRow = notaTituloLabel(nota);
                    const autor = nota.autor || "Sin usuario";
                    return (
                      <AccordionItem key={nota.id} value={itemId} className="px-3">
                        <div className="flex w-full min-w-0 items-center [&>h3]:min-w-0">
                          <AccordionTrigger className="min-w-0 py-2.5 hover:no-underline">
                            <span className="flex min-w-0 items-center gap-2 text-left">
                              <span className="min-w-0 truncate text-sm font-medium">
                                {tituloRow}
                              </span>
                              <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground tabular-nums tracking-tight">
                                {fecha || "Sin fecha"}
                              </span>
                            </span>
                          </AccordionTrigger>
                          {isAdmin && (
                            <div className="flex shrink-0 items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Editar"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  startEdit(nota);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className={`h-8 w-8 p-0 ${deleteIconButtonClass}`}
                                title="Eliminar"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setPendingDeleteIds([nota.id]);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {isAdmin && (
                            <div
                              className="ml-auto flex shrink-0 items-center pl-3"
                              onClick={stopAccordionToggle}
                              onPointerDown={stopAccordionToggle}
                              onKeyDown={stopAccordionToggle}
                            >
                              <Checkbox
                                checked={selectedNotaIds.has(nota.id)}
                                onCheckedChange={(value) =>
                                  toggleNotaSelected(nota.id, value === true)
                                }
                                onClick={(e) => e.stopPropagation()}
                                aria-label={`Seleccionar nota ${tituloRow}`}
                              />
                            </div>
                          )}
                        </div>
                        <AccordionContent className="pb-3">
                          {isEditingThis ? (
                            <div className="space-y-2">
                              <p className="text-xs text-muted-foreground">
                                {fecha || "Sin fecha"} · {autor}
                              </p>
                              <div className="space-y-1.5">
                                <Label htmlFor={`nota-titulo-${nota.id}`}>Título</Label>
                                <Input
                                  id={`nota-titulo-${nota.id}`}
                                  value={titulo}
                                  onChange={(e) => setTitulo(e.target.value)}
                                  placeholder="Título"
                                  maxLength={200}
                                  autoFocus
                                />
                              </div>
                              <div className="space-y-1.5">
                                <Label htmlFor={`nota-cuerpo-${nota.id}`}>Cuerpo</Label>
                                <Textarea
                                  id={`nota-cuerpo-${nota.id}`}
                                  value={cuerpo}
                                  onChange={(e) => setCuerpo(e.target.value)}
                                  placeholder="Escribí la nota..."
                                  rows={5}
                                />
                              </div>
                              <FormFooter
                                onBack={cancelInlineEdit}
                                backDisabled={saving}
                                onSave={() => void handleSave()}
                                saveDisabled={saving}
                                saving={saving}
                              />
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="text-xs text-muted-foreground">
                                {fecha || "Sin fecha"} · {autor}
                              </p>
                              <p className="text-sm whitespace-pre-wrap break-words">{nota.cuerpo}</p>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Nueva nota</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="nota-titulo-nueva">Título</Label>
                  <Input
                    id="nota-titulo-nueva"
                    value={titulo}
                    onChange={(e) => setTitulo(e.target.value)}
                    placeholder="Título"
                    maxLength={200}
                    autoFocus
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="nota-cuerpo-nueva">Cuerpo</Label>
                  <Textarea
                    id="nota-cuerpo-nueva"
                    value={cuerpo}
                    onChange={(e) => setCuerpo(e.target.value)}
                    placeholder="Escribí la nota..."
                    rows={5}
                  />
                </div>
              </div>
              <DialogFooter>
                <FormFooter
                  onBack={backToList}
                  backDisabled={saving}
                  onSave={() => void handleSave()}
                  saveDisabled={saving}
                  saving={saving}
                />
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingDeleteIds.length > 0}
        onOpenChange={(next) => {
          if (!next) setPendingDeleteIds([]);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteIds.length > 1
                ? `Se eliminarán ${pendingDeleteIds.length} notas. Esta acción no se puede deshacer.`
                : "Se eliminará la nota. Esta acción no se puede deshacer."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => void handleDelete()}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
